/** ProductFormModal — THE single authoritative SUNTRONIX product creation / edit experience (Product Master → Add product).
 *  Reused verbatim by every entry point: Dashboard Home quick action, Quotation line "Add product", Product Intelligence, Catalogue. */
import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BoldableTextarea } from "@/components/commercial/BoldableTextarea";
import OemSpecFetch from "@/components/technology/OemSpecFetch";
import ProductEnrichPanel, { useAutoEnrich } from "@/components/products/ProductEnrichPanel";
import { HsnPicker } from "@/components/app/HsnPicker";
import { Sparkles, XCircle, Loader2 } from "lucide-react";

export function ProductFormModal({ initial, mode, sourceId, onClose, onSaved, onViewExisting, onUseExisting, context }) {
  const [v, setV] = useState({
    product_name: "", product_code: "", sku: "", oem_part_number: "",
    brand: "", category: "", subcategory: "", product_type: "",
    model: "", description: "", hsn_sac: "", gst_percent: null,
    base_purchase_price: null, standard_selling_price: null,
    currency: "INR", warranty: "", country_availability: [],
    status: "ACTIVE", source: "MANUAL", stock_class: "", external_crm_product_id: "",
    ...initial,
  });
  const [busy, setBusy] = useState(false);
  const [brands, setBrands] = useState([]);
  const [dupes, setDupes] = useState(null);      // possible existing products
  const [ackDupe, setAckDupe] = useState(false); // "create anyway" acknowledged
  const [oemOpen, setOemOpen] = useState(false);
  const [piOpen, setPiOpen] = useState(false);
  const [pendingPi, setPendingPi] = useState(null);
  const [autoEnrich, toggleAutoEnrich] = useAutoEnrich();
  const [lastAutoPart, setLastAutoPart] = useState(initial?.oem_part_number || "");
  const [enrich, setEnrich] = useState(null);     // {id, loading, available[], product} — PI content for an EXISTING record
  const [enrichOpen, setEnrichOpen] = useState(false);
  const enrichReq = useRef(null);
  useEffect(() => {   // AUTO ENRICH: valid part number entered (debounced) → duplicate check first; new part → Product Intelligence preview, once per value
    const part = (v.oem_part_number || "").trim();
    if (!autoEnrich || mode === "edit" || part.length < 5 || part === lastAutoPart || piOpen) return;
    const t = setTimeout(async () => {
      setLastAutoPart(part);
      const m = await checkDupes();
      if (!m.length) setPiOpen(true);
    }, 900);
    return () => clearTimeout(t);
  }, [v.oem_part_number, autoEnrich, mode, lastAutoPart, piOpen]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {   // PRODUCT FOUND: part exists → ask Product Intelligence what it can add (image / specs / datasheet / warranty)
    const d = mode !== "edit" && dupes && dupes[0];
    if (!d) { enrichReq.current = null; setEnrich(null); return; }
    if (enrichReq.current === d.id) return;
    enrichReq.current = d.id;
    setEnrich({ id: d.id, loading: true, available: [], product: d });
    api.post("/product-enrich/lookup", { part_number: (v.oem_part_number || "").trim(), brand: v.brand || d.brand || null, product_id: d.id })
      .then(({ data }) => enrichReq.current === d.id && setEnrich({ id: d.id, loading: false, available: data.enrichment_available || [], product: d, source: data.technical_source }))
      .catch(() => enrichReq.current === d.id && setEnrich({ id: d.id, loading: false, available: [], product: d }));
  }, [dupes, mode]); // eslint-disable-line react-hooks/exhaustive-deps
  const onPiApplied = ({ preview, fields, image_decision, image_url, hsn_accept }) => {
    const l = preview.latest || {};
    setV((s) => ({ ...s, product_name: fields.includes("product_name") ? (l.product_name || s.product_name) : s.product_name, brand: s.brand || l.brand || "", model: fields.includes("model") ? (l.model || s.model) : s.model,
      category: s.category || l.category || "", description: fields.includes("description") ? (l.description || s.description) : s.description, warranty: fields.includes("warranty") ? (l.warranty || s.warranty) : s.warranty }));
    setPendingPi({ part_number: preview.part_number, fields, image_decision: image_decision === "URL" ? "USE" : image_decision, image_url, hsn_accept });
  };
  const [pendingOem, setPendingOem] = useState(null); // {brand, part_number} to attach after save

  const onOemImport = (result) => {
    const n = result.normalised || {};
    setV((s) => ({
      ...s,
      product_name: s.product_name || result.product_name || "",
      brand: s.brand || result.brand || "",
      model: n.mtm || s.model || "",
      description: result.smart_description || s.description || "",
      warranty: n.warranty || s.warranty || "",
      country_availability: (result.region || "").toUpperCase().startsWith("IND")
        ? ["India"] : s.country_availability,
    }));
    setPendingOem({ brand: result.brand, part_number: result.part_number });
  };

  useEffect(() => {
    api.get("/product-master/brands").then(({ data }) => setBrands(data || [])).catch(() => {});
  }, []);

  const set = (k) => (e) => setV((s) => ({ ...s, [k]: e.target ? e.target.value : e }));

  // Duplicate detection: Brand + SKU / OEM Part #.
  const checkDupes = useCallback(async () => {
    const skuOem = (v.oem_part_number || "").trim();
    if (!skuOem) { setDupes(null); return []; }
    try {
      const { data } = await api.post("/product-master/products/check-duplicate",
        { brand: v.brand, sku_oem: skuOem });
      const matches = (data.matches || []).filter((m) => m.id !== initial?.id);
      setDupes(matches.length ? matches : null);
      setAckDupe(false);
      return matches;
    } catch { setDupes(null); return []; }
  }, [v.oem_part_number, v.brand, initial]);

  const onHsn = (code, item) => {
    setV((s) => {
      const next = { ...s, hsn_sac: code };
      const g = item && (item.gst_rate ?? item.gst ?? item.gst_percent);
      if (g != null && (s.gst_percent == null || s.gst_percent === "")) next.gst_percent = Number(g);
      return next;
    });
  };

  const save = async () => {
    if (mode !== "edit" && dupes && dupes.length && !ackDupe) {
      toast.error("Possible existing product — review it, or tick 'Create anyway'");
      return;
    }
    setBusy(true);
    try {
      const numeric = ["gst_percent", "base_purchase_price", "standard_selling_price"];
      const payload = { ...v };
      numeric.forEach((k) => {
        payload[k] = payload[k] === "" || payload[k] === null ? null : Number(payload[k]);
      });
      if (typeof payload.country_availability === "string") {
        payload.country_availability = payload.country_availability.split(",").map(x => x.trim()).filter(Boolean);
      }
      let savedId = initial?.id;
      let saved = null;
      if (mode === "edit") {
        const { data } = await api.put(`/product-master/products/${initial.id}`, payload);
        saved = data;
        toast.success("Product updated");
      } else if (mode === "duplicate") {
        const { data } = await api.post(`/product-master/products/${sourceId}/duplicate`, payload);
        savedId = data?.id;
        saved = data;
        toast.success("Product duplicated");
      } else {
        const { data } = await api.post(`/product-master/products`, payload);
        savedId = data?.id;
        saved = data;
        toast.success("Product created");
      }
      // Attach the full OEM specification bundle (raw + normalised + datasheet
      // + images + source metadata) to the saved product — never commercial.
      if (pendingPi && savedId) {
        try { await api.post("/product-enrich/apply", { ...pendingPi, product_id: savedId }); }
        catch (e) { toast.error(e.response?.data?.detail || "Product saved, but Product Intelligence could not be attached"); }
      }
      if (pendingOem && savedId) {
        try {
          await api.post("/oem/import", {
            brand: pendingOem.brand, part_number: pendingOem.part_number,
            product_id: savedId, apply_specs: true,
            apply_description: false, apply_warranty: false,
          });
        } catch { /* fail-safe: product still saved without the rich spec bundle */ }
      }
      onSaved({ id: savedId, ...payload, ...(saved && typeof saved === "object" ? saved : {}) });
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d && typeof d === "object" && d.code === "DUPLICATE") {
        toast.error(d.conflicts.join(" · "));
      } else if (Array.isArray(d)) {
        toast.error(d.map((x) => `${(x.loc || []).slice(-1)[0]}: ${x.msg}`).join(" · "));
      } else if (typeof d === "string") {
        toast.error(d);
      } else if (e.response?.status) {
        toast.error(`Save failed (HTTP ${e.response.status}) — server may be waking up. Your entries are kept; please retry in a moment`);
      } else {
        toast.error("Save failed — server unreachable (it may be waking up). Your entries are kept; please retry in a moment");
      }
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-start justify-center px-3 pt-3 sm:px-4 sm:pt-8 pb-[calc(6rem+env(safe-area-inset-bottom))]"
      data-testid="product-form-modal">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-full flex flex-col overflow-hidden" data-testid="product-form-card">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between shrink-0 bg-white">
          <div>
            {context && <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500" data-testid="product-form-context">Product Master · {context}</div>}
            <div className="text-sm font-heading font-semibold">
              {mode === "edit" ? "Edit product" : mode === "duplicate" ? "Duplicate product" : "New product"}
            </div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900" data-testid="product-form-close">
            <XCircle className="h-4 w-4" />
          </button>
        </div>
        <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3 flex-1 min-h-0 overflow-y-auto" data-testid="product-form-body">
          <div className="md:col-span-2">
            <Field label="Product Name *" testId="fld-product-name">
              <Input value={v.product_name || ""} onChange={set("product_name")} placeholder="e.g. MacBook Pro 14&quot; M4" /></Field>
          </div>
          <Field label="Brand *" testId="fld-brand">
            <Input list="brand-options" value={v.brand || ""} onChange={set("brand")} onBlur={checkDupes} placeholder="Apple, Lenovo…" />
            <datalist id="brand-options">{brands.map((b) => <option key={b.id || b.name} value={b.name} />)}</datalist>
          </Field>
          <Field label="Vertical / Category *" testId="fld-category">
            <Input list="cat-options" value={v.category || ""} onChange={set("category")} placeholder="Laptops, Displays…" />
          </Field>
          <div className="md:col-span-2">
            <Field label="SKU / OEM Part # *" testId="fld-sku-oem">
              <Input value={v.oem_part_number || ""} onChange={set("oem_part_number")} onBlur={checkDupes}
                placeholder="MU963HN/A · 21ML005GIN · B0FQX3…" /></Field>
          </div>

          <div className="md:col-span-2 -mt-1">
            <Button type="button" variant="outline" size="sm" onClick={() => setPiOpen(true)}
              disabled={!(v.oem_part_number || "").trim()} data-testid="pi-fetch-open"
              className="border-indigo-200 text-indigo-700 hover:bg-indigo-50">
              <Sparkles className="h-3.5 w-3.5 mr-1.5" /> Fetch Product Intelligence
            </Button>
            <label className="ml-3 inline-flex items-center gap-1.5 text-[11px] text-slate-600" data-testid="pi-auto-enrich"><input type="checkbox" checked={autoEnrich} onChange={toggleAutoEnrich} /> Auto Enrich {autoEnrich ? "ON" : "OFF"}</label>
            {v.brand && <button type="button" onClick={() => setOemOpen(true)} className="ml-3 text-[11px] underline text-slate-500" data-testid="oem-fetch-open">OEM connector</button>}
            {pendingPi && <span className="ml-2 text-[11px] text-emerald-600 font-medium" data-testid="pi-attached-note">✓ Product Intelligence approved — save to attach</span>}
            {pendingOem && <span className="ml-2 text-[11px] text-emerald-600 font-medium" data-testid="oem-attached-note">✓ {pendingOem.brand} specs imported — save to attach</span>}
          </div>

          {dupes && dupes.length > 0 && (
            <div className="md:col-span-2 border border-amber-200 bg-amber-50 rounded-lg p-3" data-testid="product-dupe-banner">
              <div className="text-[12px] font-semibold text-amber-800 mb-1">Product already exists ({dupes.length}) — not creating a duplicate</div>
              {dupes.map((m) => (
                <div key={m.id} className="flex items-center justify-between gap-2 text-[12px] py-0.5">
                  <span className="truncate"><b>{m.product_name}</b> · {m.brand || "—"} · <span className="font-mono">{m.oem_part_number || m.sku}</span> · {m.product_code}</span>
                  <span className="flex gap-1 shrink-0">
                    {onViewExisting && <button type="button" className="h-6 px-2 rounded border border-indigo-200 bg-white text-indigo-700 font-medium text-[11px]" onClick={() => { onClose(); onViewExisting(m.id); }} data-testid={`product-dupe-view-${m.id}`}>Open product</button>}
                    {onUseExisting && <button type="button" className="h-6 px-2 rounded bg-emerald-700 text-white font-medium text-[11px]" onClick={() => { onClose(); onUseExisting(m.id); }} data-testid={`product-dupe-use-${m.id}`}>Use product</button>}
                  </span>
                </div>
              ))}
              {enrich && enrich.id === dupes[0].id && (enrich.loading || enrich.available.length > 0) && (
                <div className="mt-2 rounded-md border border-indigo-200 bg-white p-2.5" data-testid="product-found-enrich">
                  {enrich.loading ? (
                    <div className="text-[11px] text-slate-500 inline-flex items-center gap-1.5"><Loader2 className="h-3 w-3 animate-spin" /> Checking Product Intelligence for newer content…</div>
                  ) : (
                    <div className="flex flex-wrap items-center gap-2">
                      <Sparkles className="h-3.5 w-3.5 text-indigo-600" />
                      <span className="text-[11px] font-semibold text-indigo-800">PRODUCT FOUND — information available:</span>
                      {enrich.available.map((a) => <span key={a} className="rounded-full bg-indigo-50 border border-indigo-200 px-2 py-0.5 text-[10px] font-medium text-indigo-800" data-testid={`product-found-item-${a.toLowerCase().replace(/\s+/g, "-")}`}>{a}</span>)}
                      <button type="button" onClick={() => setEnrichOpen(true)} className="ml-auto h-7 px-3 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white text-[11px] font-semibold inline-flex items-center gap-1" data-testid="product-found-enrich-btn"><Sparkles className="h-3 w-3" /> ENRICH PRODUCT</button>
                    </div>
                  )}
                </div>
              )}
              {mode !== "edit" && (
                <label className="flex items-center gap-2 text-[12px] mt-2 text-amber-800">
                  <input type="checkbox" checked={ackDupe} onChange={(e) => setAckDupe(e.target.checked)} data-testid="product-dupe-ack" />
                  Create anyway (this is a different product)
                </label>
              )}
            </div>
          )}

          <div className="md:col-span-2">
            <Field label="Description *" testId="fld-description">
              <BoldableTextarea rows={3} value={v.description || ""}
                onChange={(val) => setV((s) => ({ ...s, description: val }))}
                enableSmartFormat smartContext={{ ref: v.oem_part_number || v.product_code || v.product_name || "product-master", brand: v.brand }}
                placeholder="Key specs / marketing description used on quotes." testid="pm-desc" /></Field>
          </div>

          <Field label="HSN / SAC" testId="fld-hsn">
            <HsnPicker value={v.hsn_sac} productName={v.product_name} productDesc={v.description}
              type="HSN" onChange={onHsn} testid="product-hsn" /></Field>
          <Field label="GST %" testId="fld-gst">
            <Input type="number" value={v.gst_percent ?? ""} onChange={set("gst_percent")} placeholder="e.g. 18" /></Field>

          <Field label="Standard Selling Price (optional)" testId="fld-sell-price">
            <Input type="number" value={v.standard_selling_price ?? ""} onChange={set("standard_selling_price")} /></Field>
          <Field label="Warranty (optional)" testId="fld-warranty">
            <Input value={v.warranty || ""} onChange={set("warranty")} placeholder="e.g. 1 year OEM" /></Field>

          <Field label="Status">
            <select className="h-9 border border-slate-200 rounded px-2 w-full text-[13px]"
              value={v.status || "ACTIVE"} onChange={set("status")} data-testid="fld-status">
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
            </select></Field>
          <Field label="Stock Class">
            <select className="h-9 border border-slate-200 rounded px-2 w-full text-[13px]"
              value={v.stock_class || ""} onChange={set("stock_class")} data-testid="fld-stock-class">
              <option value="">Auto (by category)</option>
              <option value="DEVICE">Device</option>
              <option value="ACCESSORY">Accessory</option>
            </select></Field>
          {v.product_code && (
            <Field label="Internal Product ID">
              <Input value={v.product_code} disabled className="bg-slate-50 font-mono text-[12px]" data-testid="fld-product-code-ro" /></Field>
          )}
        </div>
        <div className="p-4 border-t border-slate-200 flex flex-wrap items-center justify-between gap-2 shrink-0 bg-white" data-testid="product-form-footer">
          <span className="text-[11px] text-slate-400">Internal Product ID, model, cost, country & CRM mapping are managed automatically — see Product 360.</span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} data-testid="fld-cancel">Cancel</Button>
            <Button onClick={save} disabled={busy || !v.product_name || !v.brand || !v.oem_part_number || !v.category || !v.description}
              className="bg-emerald-700 hover:bg-emerald-800 text-white"
              data-testid="fld-save">
              {busy ? "Saving…" : (mode === "duplicate" ? "Create duplicate" : "Save product")}
            </Button>
          </div>
        </div>
      </div>
      <ProductEnrichPanel open={piOpen} onOpenChange={setPiOpen} partNumber={v.oem_part_number} brand={v.brand} productId={mode === "edit" ? initial?.id : null} mode="form" onApplied={onPiApplied} />
      {enrich && <ProductEnrichPanel open={enrichOpen} onOpenChange={setEnrichOpen} partNumber={v.oem_part_number} brand={v.brand || enrich.product?.brand} productId={enrich.id} mode="apply"
        onApplied={() => { toast.success(`${enrich.product?.product_code || "Product"} enriched from Product Intelligence`); onClose(); onViewExisting?.(enrich.id); }} />}
      {oemOpen && (
        <OemSpecFetch brand={v.brand} partNumber={v.oem_part_number}
          onImport={onOemImport} onClose={() => setOemOpen(false)} />
      )}
    </div>
  );
}


export function Field({ label, children, testId }) {
  return (
    <div data-testid={testId}>
      <Label className="text-[11px] text-slate-600">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

export default ProductFormModal;
